'use client'

import React from 'react';

/**
 * Enhanced PolicyCard Component
 * Designed for full-width layouts with a glassmorphism effect
 */
const PolicyCard = ({ title, children, accentColor = "from-indigo-500 to-cyan-500" }) => {
  return (
    <div className="group relative w-full mb-8 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-lg">
      {/* Top Accent Bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accentColor} opacity-70 group-hover:opacity-100 transition-opacity`} />
      
      <div className="p-6 md:p-10">
        <div className="flex flex-col gap-2 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
            {title}
          </h2>
          <div className="h-1 w-12 rounded-full bg-indigo-500/50" />
        </div>

        <div className="text-gray-700 leading-relaxed text-base md:text-lg space-y-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export default function DisclosurePage() {
  return (
    <div className="min-h-screen bg-white text-gray-700 selection:bg-indigo-500/20">
      {/* Background Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-900/20 blur-[120px]" />
        <div className="absolute top-[20%] -right-[10%] w-[30%] h-[30%] rounded-full bg-blue-900/10 blur-[100px]" />
      </div>

      <main className="relative z-10 w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16">
        
        {/* Header Section */}
        <header className="mb-16">
          <div className="inline-block px-4 py-1.5 mb-4 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-700 text-xs font-bold uppercase tracking-widest">
            Regulatory Compliance
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tighter mb-4">
            Research Services Disclosure
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl">
            Official disclosures as per SEBI (Research Analysts) Regulations, 2014 for Deepti Sarkar (Option Bricks).
          </p>
        </header>

        {/* Content Grid - Using full width with a slight gap for aesthetics */}
        <div className="flex flex-col w-full">
          
          <PolicyCard title="SEBI Regulations Disclosure" accentColor="from-blue-500 to-indigo-500">
            <p>Disclosure As Per Securities And Exchange Board Of India (Research Analysts) Regulations, 2014.</p>
          </PolicyCard>

          <PolicyCard title="About Myself" accentColor="from-indigo-500 to-blue-500">
            <p>
              I am <span className="text-gray-900 font-semibold">Deepti Sarkar</span>, a SEBI Registered research analyst 
              <span className="mx-2 px-2 py-0.5 rounded bg-gray-100 text-indigo-700 font-mono text-sm border border-gray-200">Regd No. INH000017408</span>
              and BSE Enlistment number 6282. I also hold these qualifications – MBA in Finance.
            </p>
          </PolicyCard>

          <PolicyCard title="Business Activity" accentColor="from-cyan-500 to-blue-500">
            <p>
              I am a SEBI-registered Research Analyst engaged in providing independent research services in the securities market. 
              My activities include preparing and publishing research reports, offering investment recommendations, and providing 
              analysis on equities and other financial instruments based on fundamental and/or technical research.
            </p>
          </PolicyCard>

          <PolicyCard title="Terms & Conditions for Research Reports" accentColor="from-emerald-500 to-teal-500">
            <div className="space-y-8">
              <section>
                <h3 className="text-indigo-700 font-bold mb-3 uppercase text-sm tracking-wider">Ownership & Conflicts of Interest</h3>
                <ul className="space-y-3 list-decimal pl-5">
                  <li>The Research Analyst or his relatives or associates do not have financial interest in the subject Company.</li>
                  <li>The Research Analyst or his relatives or associates do not have actual/beneficial ownership of one per cent or more securities of the subject Company, at the end of the month immediately preceding the date of publication of the research report.</li>
                  <li>The Research Analyst or his relatives or associates do not have any other material conflict of interest at the time of publication of the research report.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-indigo-700 font-bold mb-3 uppercase text-sm tracking-wider">Receipt of Compensation</h3>
                <ul className="space-y-3 list-decimal pl-5">
                  <li>The Research Analyst or his associates have not received compensation from the subject Company in the past twelve months.</li>
                  <li>The Research Analyst or his associates have not managed or co-managed public offering of securities for the subject Company in the past twelve months.</li>
                  <li>The Research Analyst or his associates have not received any compensation for investment banking or merchant banking or brokerage services from the subject Company in the past twelve months.</li>
                  <li>The Research Analyst or his associates have not received any compensation or other benefits from the subject Company or third party in connection with the research report.</li>
                </ul>
              </section>
            </div>
          </PolicyCard>

          {/* <PolicyCard title="General Disclosure" accentColor="from-blue-500 to-indigo-500">
            <ul className="space-y-3 list-decimal pl-5">
              <li>The Research Analyst has not served as an officer, director or employee of the subject Company.</li>
              <li>The Research Analyst has not been engaged in market making activity for the subject Company.</li>
              <li>This report is generated with the assistance of <span className="text-gray-900 font-semibold">artificial intelligence (AI) tools</span> for analytical and drafting purposes.</li>
            </ul>
          </PolicyCard> */}




<PolicyCard
  title="General Disclosure"
  accentColor="from-blue-500 to-indigo-500"
>
  <ul className="space-y-3 list-decimal pl-5">
    <li>
      The Research Analyst has not served as an officer, director or
      employee of the subject Company.
    </li>

    <li>
      The Research Analyst has not been engaged in market making
      activity for the subject Company.
    </li>

    <li>
      This report is generated with the assistance of{" "}
      <span className="text-gray-900 font-semibold">
        artificial intelligence (AI) tools
      </span>{" "}
      for analytical and drafting purposes.
    </li>
  </ul>

  <div className="mt-6 border-t border-gray-200 pt-5">
    <h3 className="mb-3 text-base font-semibold text-gray-900">
      Disclosure on the Use of Artificial Intelligence (AI)
    </h3>

    <div className="space-y-4 text-sm leading-6 text-gray-700">
      <p>
        <span className="font-semibold text-gray-900">
          Scope &amp; Extent of AI Use:
        </span>{" "}
        We utilize Artificial Intelligence (LLM technology - ChatGPT)
        strictly as an assistance tool for initial language drafting,
        structural formatting, and text polishing of research documents.
      </p>

      <p>
        <span className="font-semibold text-gray-900">
          Data Analysis &amp; Core Research:
        </span>{" "}
        All data aggregation, mathematical calculations, chart generation,
        financial evaluation, stock valuation, and investment recommendations
        are executed independently using proprietary analysis, standard
        data-processing tools, and manual calculations. AI models are not
        used to generate investment decisions, price targets, or financial
        recommendations.
      </p>
    </div>
  </div>
</PolicyCard>


          <PolicyCard title="Definition of Terms" accentColor="from-indigo-600 to-purple-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="mb-2"><strong className="text-gray-900 text-lg">1. Buy:</strong></p>
                <p className="text-sm opacity-80">Stock recommended to be bought either at Market or within price band mentioned. CMP orders must be executed near closing time.</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="mb-2"><strong className="text-gray-900 text-lg">2. Sell / Target:</strong></p>
                <p className="text-sm opacity-80">The price at which stock must be sold. Stocks reaching or trading above target must be exited immediately.</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="mb-2"><strong className="text-gray-900 text-lg">3. Hold:</strong></p>
                <p className="text-sm opacity-80">No exit required. Analyst believes the stock may go beyond target or has not yet hit stop loss.</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="mb-2"><strong className="text-gray-900 text-lg">4. Stop Loss:</strong></p>
                <p className="text-sm opacity-80">Mandatory exit price due to adverse movement. If stock trades at or below stop loss near closing, it must be exited.</p>
              </div>
            </div>
          </PolicyCard>

          <PolicyCard title="Contact & Grievance" accentColor="from-slate-400 to-slate-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase text-gray-500 mb-1">Brand Name</p>
                <p className="text-xl font-bold text-gray-900 mb-4">Option Bricks</p>
                <p className="text-sm">Flat No. D08/4, Siddhivinayak Chs Ltd, Sector-08, Sanpada, NAVI MUMBAI, MAHARASHTRA, 400705.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <p className="text-xs uppercase text-indigo-700 mb-1 font-bold">Grievance Officer</p>
                <p className="text-lg font-bold text-gray-900">Deepti Sarkar</p>
                <p className="text-sm mt-2 text-gray-600">Phone: 9082280240</p>
                <p className="text-sm text-gray-600">Email: deeptisarkar7@gmail.com</p>
              </div>
            </div>
          </PolicyCard>

          {/* Legal Bars */}
          <div className="mt-12 space-y-6">
             <div className="p-8 rounded-3xl bg-red-500/10 border border-red-500/20">
                <h2 className="text-red-600 font-bold mb-2 flex items-center gap-2">
                  <span className="text-2xl">⚠️</span> Warning
                </h2>
                <p className="text-red-800 text-lg italic font-medium">
                  “Investments in securities market are subject to market risks. Read all the related documents carefully before investing.”
                </p>
             </div>

             <div className="p-8 rounded-3xl bg-cyan-500/10 border border-cyan-500/20">
                <h2 className="text-indigo-700 font-bold mb-2">Disclaimer</h2>
                <p className="text-gray-700 text-lg italic">
                  “Registration granted by SEBI, membership of a SEBI recognized supervisory body (if any) and certification from NISM in no way guarantee performance of the intermediary or provide any assurance of returns to investors.”
                </p>
             </div>
          </div>

        </div>

        {/* <footer className="mt-24 pb-10 text-center text-gray-600 text-sm border-t border-gray-100 pt-8">
          © {new Date().getFullYear()} Option Bricks. All rights reserved. SEBI Registered Research Analyst.
        </footer> */}

      </main>
    </div>
  );
}